import Vue from 'vue';
import adminApi from './common';
import { getToken } from './auth';

const DEFAULT_OPTIONS = {
  emulateJSON: true
};

const NO_TOKEN_PATHS = [
  '/Login/login'
];

let isInstalled = false;

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function normalizePath(path) {
  if (!path) {
    return '';
  }

  if (path.indexOf(adminApi.api_url) === 0) {
    return path.slice(adminApi.api_url.length) || '/';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return path.charAt(0) === '/' ? path : `/${path}`;
}

function resolveUrl(path) {
  const normalizedPath = normalizePath(path);
  if (!normalizedPath) {
    return adminApi.api_url;
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return normalizedPath;
  }

  return `${adminApi.api_url}${normalizedPath}`;
}

function shouldInjectToken(path, options) {
  if (options && options.withToken === false) {
    return false;
  }

  const normalizedPath = normalizePath(path);
  return NO_TOKEN_PATHS.indexOf(normalizedPath) === -1;
}

function injectTokenToPayload(payload) {
  const token = getToken();
  if (!token) {
    return payload;
  }

  if (payload === undefined || payload === null) {
    return {
      token
    };
  }

  if (!isPlainObject(payload)) {
    return payload;
  }

  if (payload.token) {
    return payload;
  }

  return {
    ...payload,
    token
  };
}

function createRequestOptions(options) {
  return {
    ...DEFAULT_OPTIONS,
    ...(options || {})
  };
}

function serializeParams(params) {
  if (!params || !isPlainObject(params)) {
    return '';
  }

  return Object.keys(params).reduce((result, key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') {
      return result;
    }

    result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    return result;
  }, []).join('&');
}

function createApiMethod(method) {
  return function request(path, payload, options) {
    const requestOptions = createRequestOptions(options);
    const requestUrl = resolveUrl(path);
    const shouldAddToken = shouldInjectToken(path, requestOptions);
    const requestPayload = shouldAddToken ? injectTokenToPayload(payload) : payload;

    delete requestOptions.withToken;

    return Vue.http[method](requestUrl, requestPayload, requestOptions);
  };
}

const apiClient = {
  post: createApiMethod('post'),
  get(path, params, options) {
    const requestOptions = createRequestOptions(options);
    const requestUrl = resolveUrl(path);
    const shouldAddToken = shouldInjectToken(path, requestOptions);
    const requestParams = shouldAddToken ? injectTokenToPayload(params) : params;

    delete requestOptions.withToken;

    return Vue.http.get(requestUrl, {
      ...requestOptions,
      params: requestParams
    });
  },
  buildUrl(path, params, options) {
    const requestOptions = options || {};
    const requestParams = shouldInjectToken(path, requestOptions) ? injectTokenToPayload(params) : params;
    const queryString = serializeParams(requestParams);
    const requestUrl = resolveUrl(path);

    if (!queryString) {
      return requestUrl;
    }

    return `${requestUrl}${requestUrl.indexOf('?') === -1 ? '?' : '&'}${queryString}`;
  }
};

export function installApiClient(VueInstance) {
  if (isInstalled) {
    return apiClient;
  }

  isInstalled = true;

  VueInstance.http.interceptors.push((request, next) => {
    const shouldAddToken = shouldInjectToken(request.url, request);

    if (shouldAddToken) {
      if (request.method === 'get') {
        request.params = injectTokenToPayload(request.params);
      } else {
        request.body = injectTokenToPayload(request.body);
      }
    }

    next();
  });

  VueInstance.prototype.$api = apiClient;

  return apiClient;
}

export default apiClient;



// WEBPACK FOOTER //
// ./src/util/api.js